import React from "react";
import {
	Button,
	Column,
	Conditional,
	Else,
	forceUseHookDependencies,
	If,
	isAfter,
	makeClassName,
	Modal,
	Row,
	Tooltip,
	TooltipProps,
	TruncatedText,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import {
	CalendarEvent,
	useCalendarEventCalendarDetailQuery,
	useCalendarEventUserListQuery,
} from "../../../../Utilities";
import { CalendarEventFormat } from "../../../Format";
import { CalendarEventUtilities } from "../../../Utilities";
import { ControlledCalendarEventAdminModal } from "../AdminModal";
import { AttendeesRow } from "../General";
import { ControlledCalendarEventModal } from "../Modal";
import { RSVPForm } from "../RSVPForm";
import styles from "./styles.module.css";

export type CalendarEventTooltipProps = {
	calendarEventID: CalendarEvent["id"];
	isOpen: boolean;
	synchronizeContent?: () => void;
	withModalBackButton?: boolean;
	modalVariant?: "default" | "admin";
	setIsModalOpen?: (value: boolean) => void; // TODO better handling for nested opening with useClickOutListener
} & Omit<TooltipProps, "isInteractable">;

export function CalendarEventTooltip({
	calendarEventID,
	isOpen,
	synchronizeContent,
	withModalBackButton,
	modalVariant = "default",
	setIsModalOpen,
	className,
	...props
}: CalendarEventTooltipProps) {
	const { loading, data } = useCalendarEventCalendarDetailQuery({ variables: { id: calendarEventID }, skip: !isOpen });
	const calendarEvent = data?.calendarEventConnection?.edges[0]?.node;
	const hasCalendarEvent = !!calendarEvent;

	const viewerResponse = calendarEvent?.calendarEventResponses.find((response) => response.author?.isCurrent) ?? null;

	const isPastEvent = !!calendarEvent && isAfter(new Date(), CalendarEventUtilities.endTimeDate(calendarEvent), "day");

	React.useEffect(() => {
		forceUseHookDependencies(loading, hasCalendarEvent);
		if (synchronizeContent) {
			synchronizeContent();
		}
	}, [synchronizeContent, loading, hasCalendarEvent]);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();
	React.useEffect(() => {
		if (setIsModalOpen) {
			setIsModalOpen(isModalOpen);
		}
	}, [setIsModalOpen, isModalOpen]);

	const ModalComponent = modalVariant === "admin" ? ControlledCalendarEventAdminModal : ControlledCalendarEventModal;

	return (
		<Tooltip isInteractable {...props} className={makeClassName(styles["calendar-event-tooltip"], className)}>
			<Tooltip.Body>
				<Conditional>
					<If condition={!calendarEvent && loading}>Loading...</If>
					<Else>
						{!!calendarEvent && (
							<Column justify="spaced-start">
								<Row
									justify="spaced-start"
									align="center"
									className={
										styles["calendar-event-tooltip__detail--main"] +
										" " +
										styles["calendar-event-tooltip__detail--name"]
									}
								>
									<Icon.CalendarBlank /> {calendarEvent.name}
								</Row>

								<Row justify="spaced-start" align="center" className={styles["calendar-event-tooltip__detail--main"]}>
									<Icon.Clock /> {CalendarEventFormat.timeRange(calendarEvent)}
								</Row>

								<If condition={calendarEvent.location !== ""}>
									<Row justify="spaced-start" align="center" className={styles["calendar-event-tooltip__detail--main"]}>
										<Icon.MapPin /> <TruncatedText>{calendarEvent.location}</TruncatedText>
									</Row>
								</If>

								<CalendarEventTooltipAttendeesSection calendarEventID={calendarEventID} isOpen={isOpen} />

								<If condition={calendarEvent.allowsResponses && !isPastEvent}>
									<Column justify="spaced-start" verticalSpacing="0.375rem">
										<Row
											justify="spaced-start"
											align="center"
											className={styles["calendar-event-tooltip__detail--main"]}
										>
											<Icon.EnvelopeSimple /> RSVP
										</Row>
										<div
											className={
												styles["calendar-event-tooltip__detail__details"] +
												" " +
												styles["calendar-event-tooltip__detail__details--rsvp-form"]
											}
										>
											<RSVPForm calendarEventID={calendarEventID} calendarEventResponse={viewerResponse} />
										</div>
									</Column>
								</If>

								<Row justify="flex-end">
									<Button variant="link" size="small" onClick={openModal}>
										View
									</Button>

									<Modal.If condition={isModalOpen}>
										<ModalComponent
											calendarEventID={calendarEventID}
											withBackButton={withModalBackButton}
											onClose={closeModal}
										/>
									</Modal.If>
								</Row>
							</Column>
						)}
					</Else>
				</Conditional>
			</Tooltip.Body>
		</Tooltip>
	);
}

type CalendarEventTooltipAttendeesSectionProps = {
	calendarEventID: CalendarEvent["id"];
	isOpen: boolean;
};

function CalendarEventTooltipAttendeesSection({ calendarEventID, isOpen }: CalendarEventTooltipAttendeesSectionProps) {
	const { data } = useCalendarEventUserListQuery({
		variables: {
			calendarEventID,
			pagination: { first: 10 },
		},
		skip: !isOpen,
	});
	const totalUsers = data?.userConnection.totalCount ?? 0;
	const users = React.useMemo(() => data?.userConnection.edges.map((edge) => edge?.node) ?? [], [data]);

	if (totalUsers === 0) {
		return null;
	}

	return (
		<Column justify="spaced-start" verticalSpacing="0.375rem">
			<Row justify="spaced-start" align="center" className={styles["calendar-event-tooltip__detail--main"]}>
				<Icon.Users /> {totalUsers} Invited
			</Row>
			<div className={styles["calendar-event-tooltip__detail__details"]}>
				<AttendeesRow
					users={users}
					numAdditional={totalUsers - users.length}
					maxDisplayed={10}
					horizontalSpacing={4}
					iconSize={20}
				/>
			</div>
		</Column>
	);
}
